<ng-template #reportPopupTemplate>
  <div
    class="position-relative p-3 rounded-2 text-lightgray bg-card bg-dark border border-darkgray"
    style="width: min(540px, 100vw)"
  >
    <span
      class="rb-ic rb-ic-close fs-2xl position-absolute top-2 end-2 cursor-pointer hover-effect"
      (click)="closeForm()"
    ></span>

    @if (!reportSubmitted()) {
      <span class="fs-lg text-body-emphasis d-block mb-3">Report Map Issue</span>

      <form #reportForm="ngForm" (ngSubmit)="reportForm.valid && submitReport()">
      <div class="row g-3 align-items-end">
        <div class="col-12">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input
            type="text"
            class="form-control text-lightgray bg-darkgray border border-darkgray"
            data-cy="map-report-title"
            maxlength="255"
            name="title"
            required
            pattern=".*\S.*"
            placeholder="Enter title"
            [(ngModel)]="report.title"
          />
        </div>

        <div [class]="canBeDigital() ? 'col-md-3' : 'col-md-6'">
          <label class="form-label">Sign type:</label>
          <select
            class="form-select text-lightgray bg-darkgray border border-darkgray"
            [(ngModel)]="report.signType"
            name="signType"
            (change)="onSignTypeChange()"
          >
            @for (sign of signTypes; track sign) {
              <option [value]="sign">{{ sign }}</option>
            }
          </select>
        </div>

        @if (report.signType.startsWith('SPEED_LIMIT')) {
          <div class="col-md-3">
            <label class="form-label">Speed:</label>
            <input
              type="number"
              class="form-control text-lightgray bg-darkgray border border-darkgray"
              [(ngModel)]="report.speedLimit"
              name="speedLimit"
              (change)="onSpeedChange()"
              [min]="isZoneSign() ? 20 : 10"
              [max]="isZoneSign() ? 30 : 130"
              step="10"
            />
          </div>
        }

        <div class="col-md-3">
          <label class="form-label">Bearing:</label>
          <input
            type="number"
            class="form-control text-lightgray bg-darkgray border border-darkgray"
            [(ngModel)]="report.bearing"
            name="bearing"
            (change)="onBearingChange()"
            min="0"
            max="359"
            step="5"
          />
        </div>

        @if (canBeDigital()) {
          <div class="col-md-3">
            <label class="form-label">Display type:</label>
            <select
              class="form-select text-lightgray bg-darkgray border border-darkgray"
              [(ngModel)]="report.displayType"
              name="displayType"
              (change)="onDisplayTypeChange()"
            >
              @for (dt of signDisplayTypes; track dt) {
                <option [value]="dt">{{ dt }}</option>
              }
            </select>
          </div>
        }

        <div class="col-12">
          <label class="form-label">Supplementary signs:</label>
          <select
            class="form-select text-lightgray bg-darkgray border border-darkgray"
            multiple
            [(ngModel)]="report.supplementarySigns"
            name="supplementarySigns"
            (change)="onSupplementaryChange()"
          >
            @for (sup of supplementarySignTypes; track sup) {
              <option [value]="sup">{{ sup }}</option>
            }
          </select>
          @if (report.supplementarySigns.length) {
            <div class="mt-2">
              @for (sup of report.supplementarySigns; track sup) {
                <span
                  class="badge bg-darkgray me-1"
                  style="cursor: pointer"
                  (click)="removeSupplementarySign(sup)"
                  title="Remove"
                >
                  {{ sup }} <span class="ms-1">&times;</span>
                </span>
              }
            </div>
          }
        </div>

        <div class="col-md-6">
          <label class="form-label">Category <span class="text-danger">*</span></label>
          <select
            class="form-select text-lightgray bg-darkgray border border-darkgray"
            data-cy="map-report-category"
            name="category"
            required
            [(ngModel)]="report.category"
          >
            <option value="" disabled>Select a category</option>
            @for (cat of categories; track cat.value) {
              <option [value]="cat.value">{{ cat.label }}</option>
            }
          </select>
        </div>

        <div class="col-md-6">
          <label class="form-label"
            >Occurrence Date &amp; Time <span class="text-danger">*</span></label
          >
          <input
            type="datetime-local"
            name="occurrenceDateTime"
            required
            class="form-control text-lightgray bg-darkgray border border-darkgray"
            data-cy="map-report-datetime"
            [(ngModel)]="report.occurrenceDateTime"
          />
        </div>

        <div class="col-12">
          <label class="form-label">Description <span class="text-danger">*</span></label>
          <textarea
            class="form-control text-lightgray bg-darkgray border border-darkgray"
            rows="3"
            name="description"
            required
            pattern="[\s\S]*\S[\s\S]*"
            placeholder="Describe the issue..."
            data-cy="map-report-description"
            [(ngModel)]="report.description"
          ></textarea>
        </div>
      </div>

      @if (reportForm.submitted && reportForm.invalid) {
        <div class="text-danger mt-3 small" role="alert">
          Enter a title, occurrence date and time, category, and description. Check numeric field ranges.
        </div>
      }
      <div class="d-flex justify-content-end gap-2 mt-3">
        <button type="button" class="rb-btn rb-btn-outline-secondary" (click)="closeForm()">
          Cancel
        </button>
        <button
          type="submit"
          class="rb-btn rb-btn-outline-secondary"
          data-cy="map-report-submit"
          [disabled]="reportSubmitting()"
        >
          {{ reportSubmitting() ? 'Submitting...' : 'Submit Report' }}
        </button>
      </div>
      @if (reportError()) {
        <div class="text-danger mt-2 small">Submission failed. Please try again.</div>
      }
      </form>
    } @else {
      <div class="text-center py-3">
        <span class="rb-ic rb-ic-checkmark-frame fs-3xl text-success d-block mb-2"></span>
        <span class="fs-lg text-body-emphasis d-block mb-1" data-cy="map-report-success"
          >Report submitted</span
        >
        <span class="text-lightgray d-block mb-2">Thank you for your feedback.</span>
        @if (ticketKey() && ticketUrl()) {
          <span class="d-block mb-3">
            <span class="text-lightgray">Ticket: </span>
            <a
              [href]="ticketUrl()"
              target="_blank"
              class="text-success"
              data-cy="map-report-ticket-link"
              >{{ ticketKey() }}</a
            >
          </span>
        }
        <button
          type="button"
          class="rb-btn rb-btn-primary"
          (click)="closeForm()"
          data-cy="map-report-close"
        >
          Close
        </button>
      </div>
    }
  </div>
</ng-template>

@if (isMobileFormOpen()) {
  <div class="mobile-report-form position-fixed start-0 end-0 bottom-0 z-5">
    <ng-container [ngTemplateOutlet]="reportPopupTemplate"></ng-container>
  </div>
}
